import React, { createContext, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { apiSetActiveRevision } from 'client/api';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { moment } from 'client/lib/moment';
import { routes } from 'client/lib/routes';
import { InstallableApp } from 'client/model/skill';
import { getRevisionSlug } from 'client/utils/time';
import * as keyset from 'i18n/RevisionStatus';

type Props = React.PropsWithChildren<{
    installableApp: InstallableApp;
    setInstallableApp: (app: InstallableApp) => void;
}>;

const t = i18n(keyset);

const REVISION_TOAST = 'REVISION_TOAST';

interface IRevisionContext {
    handleNavigateToRevision: (revision: InstallableApp['revisions'][number]) => void;
    handleMoveToDraft: (revisionId: string) => Promise<void>;
    handleOpenPublishRevisionModal: (revisionId: string) => void;
}

const REVISION_CONTEXT_INITIAL_VALUES: IRevisionContext = {
    handleNavigateToRevision: (_revision: InstallableApp['revisions'][number]) => {},
    handleMoveToDraft: async (_revisionId: string) => {},
    handleOpenPublishRevisionModal: (_revisionId: string) => {},
};

export const RevisionContext = createContext(REVISION_CONTEXT_INITIAL_VALUES);

interface ConfirmSetActiveRevisionModalProps {
    onPublish: (revisionId: string) => void;
    onClose: () => void;
    appRevision: InstallableApp['revisions'][number];
    loading: boolean;
}

const ConfirmSetActiveRevisionModal = ({
    appRevision,
    onPublish,
    onClose,
    loading,
}: ConfirmSetActiveRevisionModalProps) => {
    const formattedDate = moment(appRevision.publishedAt).format('DD MMMM, HH:mm');

    return (
        <ModalWithConfirmation
            title={t('revision-modal-title') + formattedDate}
            text={t('revision-modal-content', { revisionDate: formattedDate })}
            onClose={onClose}
            onConfirm={() => onPublish(appRevision.id)}
            confirmButtonLabel={t('revision-apply')}
            loading={loading}
        />
    );
};

export const RevisionAction = ({ children, installableApp, setInstallableApp }: Props) => {
    const [isPendingPublish, setIsPendingPublish] = useState(false);
    const { add: addToast } = useToaster();
    const [revisionCandidate, setRevisionCandidate] = useState<InstallableApp['revisions'][number] | undefined>();
    const navigate = useNavigate();

    const handleNavigateToRevision = useCallback(
        (revision: InstallableApp['revisions'][number]) => {
            navigate(
                routes.app.installableAppRevision(installableApp.channel, installableApp.id, getRevisionSlug(revision)),
            );
        },
        [navigate, installableApp],
    );

    const handleMoveToDraft = useCallback(
        async (revisionId: string) => {
            const revisionDraft = installableApp.revisions.find((revision) => revision.id === revisionId);
            if (!revisionDraft) {
                console.error('@RevisionStatus Error: no such revision');
                return;
            }
            setInstallableApp({ ...installableApp, draft: { ...revisionDraft } } as InstallableApp);
            addToast({ title: t('revision-draft-update'), theme: 'success', name: REVISION_TOAST });
            navigate(routes.app.appPage(installableApp.channel, true, 'settings', installableApp.id));
        },
        [addToast, navigate, installableApp, setInstallableApp],
    );

    const handlePublish = useCallback(
        async (revisionId: string) => {
            setIsPendingPublish(true);

            try {
                const result = await apiSetActiveRevision(installableApp.id, installableApp.channel, revisionId);
                if (result.id && result.channel && result.draft) {
                    setInstallableApp(result);
                    addToast({ title: t('revision-publish-success'), theme: 'success', name: REVISION_TOAST });
                    navigate(routes.app.appPage(installableApp.channel, false, 'settings', installableApp.id));
                } else {
                    addToast({ title: t('revision-publish-error'), theme: 'danger', name: REVISION_TOAST });
                }
            } catch (error) {
                addToast({
                    title: t('revision-publish-error') + String(error).slice(0, 30) + '...',
                    theme: 'danger',
                    name: REVISION_TOAST,
                });
            }

            setRevisionCandidate(undefined);
            setIsPendingPublish(false);
        },
        [addToast, installableApp, setInstallableApp, navigate],
    );

    const handleOpenPublishRevisionModal = (revisionId: string) => {
        const revisionDraft = installableApp.revisions.find((revision) => revision.id === revisionId);
        if (!revisionDraft) {
            console.error('@RevisionStatus Error: no such revision');
            return;
        }
        setRevisionCandidate(revisionDraft);
    };

    return (
        <ErrorBoundary>
            <div>
                <RevisionContext.Provider
                    value={{
                        handleMoveToDraft,
                        handleNavigateToRevision,
                        handleOpenPublishRevisionModal,
                    }}
                >
                    {children}
                </RevisionContext.Provider>
                {revisionCandidate && (
                    <ConfirmSetActiveRevisionModal
                        appRevision={revisionCandidate}
                        onPublish={handlePublish}
                        onClose={() => setRevisionCandidate(undefined)}
                        loading={isPendingPublish}
                    />
                )}
            </div>
        </ErrorBoundary>
    );
};
